import { EventEmitter } from 'events';

import {
  Commander,
  CommanderAction,
  CommanderActionEvents,
  CommanderActionPhasesEnum,
  CommanderActionTypesEnum,
} from '../types.js';

/**
 * Base Action class
 */
export abstract class BaseAction<Type extends CommanderActionTypesEnum, TPayload>
  extends EventEmitter<CommanderActionEvents>
  implements CommanderAction
{
  type: Type;
  payload: TPayload | null = null;
  phase: CommanderActionPhasesEnum = CommanderActionPhasesEnum.Start;
  commander: Commander;

  constructor(commander: Commander, type: Type, payload?: TPayload | null) {
    super();
    this.type = type;
    this.payload = payload ?? null;
    this.commander = commander;
  }
  /**
   * This performs an update on the action performed in staging area
   */
  update(payload: TPayload) {
    if (this.phase === CommanderActionPhasesEnum.End) {
      throw new Error('Action is already committed, cannot be updated');
    }

    this.payload = payload;
    this.phase = CommanderActionPhasesEnum.Update;
    this._apply();
    this._onAfterApply();
    this.emit('update');
  }

  /**
   * This commits the action to be performed in the staging area
   */
  commit() {
    if (this.payload === null) {
      throw new Error('Action payload empty');
    }

    if (this.phase === CommanderActionPhasesEnum.End) {
      throw new Error('Action is already committed');
    }

    if (this.phase === CommanderActionPhasesEnum.Start) {
      this._apply();
      this._onAfterApply();
    }
    this._onBeforeCommit();
    this.commander.staging.commit(this.type);
    this.phase = CommanderActionPhasesEnum.End;
    this.emit('commit');
  }

  abort() {
    this.phase = CommanderActionPhasesEnum.End;
    this.commander.staging.abort();
    this.emit('abort');
  }

  /**
   * Apply the action to the commander
   */
  _apply(): void {
    throw new Error('Method not implemented.');
  }

  /**
   * Side effects to be performed after the action is applied
   */
  _onAfterApply(): void {}

  /**
   * Hook to apply any final staging actions before commit
   */
  _onBeforeCommit(): void {}

  /**
   *
   */
  toJSON() {
    return {
      type: this.type,
      payload: this.payload,
    };
  }

  fromJSON(json: { type: Type; selector: string; payload: TPayload }) {
    this.type = json.type;
    this.payload = json.payload;

    return this;
  }
}
